import { Fragment, useEffect } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import EnhancedRedirect from './components/Redirect';
import withAuth from 'HOC/withAuth';
import withBaseLayout from './HOC/withBaseLayout/withBaseLayout';
import withNewLayout from './HOC/withNewLayout';
import withLoadable from './HOC/withLoadable/withLoadable';
import withNewLayoutNotLogged from './HOC/withNewLayoutNotLogged';
import withBusinessLayout from '../Business/HOC/withBusinessLayout';
import withProfilesAlphaFlag from 'modules/FundingSearch/withProfilesAlphaFlag';
import LoggedInRoute from './components/LoggedInRoute/LoggedInRoute';
import LoggedBusinessRoute from './components/LoggedBusinessRoute/LoggedBusinessRoute';
import NotFound from './components/NotFound/NotFound';
import NotLoggedInRoute from './components/NotLoggedInRoute/NotLoggedInRoute';
import Onboarding from 'modules/Onboarding/containers/Onboarding';
import { SSEHandler, adapterReduxStore } from '@capitalise/common';
import { useStore } from 'react-redux';

const CloudConnectionPage = withAuth(
  withLoadable(() => import('RemoteConnection/CloudConnectionPage')),
);

const LoginRedirect = withLoadable(() => import('modules/Auth/containers/LoginRedirect'));
const Login = withLoadable(() => import('RemoteAuth/Login'));
const SimulateTypeError = withLoadable(() => import('components/SimulateErrors/SimulateTypeError'));
const SimulateChunkError = withLoadable(() =>
  import('components/SimulateErrors/SimulateChunkError'),
);

const Billing = withAuth(
  withBusinessLayout(withLoadable(() => import('RemoteSubscription/BillingPage'))),
);

const FailedPayment = withAuth(withLoadable(() => import('RemoteSubscription/FailedPayment')));

const SuccessPayment = withAuth(withLoadable(() => import('RemoteSubscription/SuccessPayment')));

const UserPortal = withAuth(withLoadable(() => import('RemoteSubscription/UserPortal')));

const ReviewPage = withAuth(withLoadable(() => import('RemoteSubscription/ReviewPage')));
const SuccessPage = withAuth(withLoadable(() => import('RemoteSubscription/SuccessPage')));
const FailPage = withAuth(withLoadable(() => import('RemoteSubscription/FailPage')));
const CongratsPage = withAuth(withLoadable(() => import('RemoteSubscription/CongratsPage')));

const Callback = withLoadable(() => import('modules/Auth/containers/Callback/Callback'));
const FundingDashboardPage = withLoadable(() =>
  import('modules/FundingDashboard/containers/FundingDashboardPage/FundingDashboardPage'),
);
const FundingReportPage = withLoadable(() =>
  import('modules/FundingReport/containers/FundingReport/FundingReport'),
);
const LoginPage = withLoadable(() => import('modules/Auth/containers/LoginPage/LoginPage'));
const MFAWarningPage = withLoadable(() =>
  import('modules/Auth/containers/MFAWarningPage/MFAWarningPage'),
);
const NetworkSignupPage = withLoadable(() => import('modules/Auth/containers/NetworkSignup'));
const SignupQuestionnaireGuardian = withLoadable(() =>
  import('modules/AuthAlpha/containers/QuestionnaireSignup/SignupQuestionnaireGuardian'),
);
const NotifyContainer = withLoadable(() => import('modules/NotifyContainer'));
const PreRequestModal = withLoadable(() => import('components/modal/PreRequestModal'));
const UpdateEnquiryStatusModal = withLoadable(() =>
  import('components/modal/UpdateEnquiryStatusModal'),
);
const RequestHelpModal = withLoadable(() => import('components/modal/RequestHelpModal'));
const OfferModal = withLoadable(() => import('components/modal/OfferModal'));

const OpenIdSignupPage = withLoadable(() => import('modules/Auth/containers/OpenIdSignup'));
const RejectPage = withLoadable(() => import('modules/Invite/containers/RejectPage/RejectPage'));
const LessonPage = withLoadable(() => import('modules/Learning/containers/LessonPage/LessonPage'));
const CapitalReport = withLoadable(() => import('modules/CapitalReport/containers/CapitalReport'));

const DashboardPage = withNewLayout(
  withLoadable(() => import('modules/DashboardAlpha/containers/DashboardPage')),
);
const FundingDashboardPageWithBaseLayout = withBaseLayout(FundingDashboardPage);
const FundingReportPageWithBaseLayout = withBaseLayout(FundingReportPage);
const FundingSearchMatchesPageWithBaseLayout = withAuth(
  withBaseLayout(
    withLoadable(() =>
      import('modules/FundingSearch/containers/FundingSearchMatchesPage/FundingSearchMatchesPage'),
    ),
  ),
);
const FundingSearchOffersPageWithBaseLayout = withBaseLayout(
  withLoadable(() =>
    import('modules/FundingSearch/containers/FundingSearchOffersPage/FundingSearchOffersPage'),
  ),
);
const FundingSearchProfilePage = withProfilesAlphaFlag(
  withNewLayout(
    withLoadable(() => import('modules/FundingSearchAlpha/containers/FundingSearchProfilePage')),
  ),
  withBaseLayout(
    withLoadable(() =>
      import('modules/FundingSearch/containers/FundingSearchProfilePage/FundingSearchProfilePage'),
    ),
  ),
);
const FundingSearchTimelinePageWithBaseLayout = withBaseLayout(
  withLoadable(() =>
    import('modules/FundingSearch/containers/FundingSearchTimelinePage/FundingSearchTimelinePage'),
  ),
);
const CapitalReportWithBaseLayout = withNewLayout(CapitalReport);
const MonitorWithBaseLayout = withAuth(
  withNewLayout(withLoadable(() => import('modules/MonitorAlpha/containers/MonitorPage'))),
);

const CasePageWithBaseLayout = withNewLayout(
  withLoadable(() => import('modules/Cases/containers/CasePage')),
);
const CasesPageWithBaseLayout = withAuth(
  withNewLayout(withLoadable(() => import('modules/Cases/containers/CasesPage'))),
);
const QuestionnairePageWithBaseLayout = withNewLayout(
  withLoadable(() => import('modules/Questionnaire')),
);
const QuestionnairePageNotLogged = withNewLayoutNotLogged(
  withLoadable(() => import('modules/Questionnaire/Anonymous')),
);
const CoursePage = withNewLayout(
  withLoadable(() => import('modules/LearningAlpha/containers/CoursePage')),
);
const LearningPage = withNewLayout(
  withLoadable(() => import('modules/LearningAlpha/containers/LearningPage')),
);

const InvitePage = withBaseLayout(
  withLoadable(() => import('modules/Invite/containers/InvitePage/InvitePage')),
);
const SignInPageAlpha = withNewLayoutNotLogged(
  withLoadable(() => import('modules/AuthAlpha/containers/SignIn/SignIn')),
);
const InviteXeroPage = withNewLayoutNotLogged(
  withLoadable(() => import('modules/AuthAlpha/containers/SSOSignup/SSOSignup')),
);
const InviteCompanyPageAlpha = withNewLayoutNotLogged(
  withLoadable(() => import('modules/AuthAlpha/containers/CompanySignup/CompanySignup')),
);
const InviteNetworkPageAlpha = withNewLayoutNotLogged(
  withLoadable(() => import('modules/AuthAlpha/containers/NetworkSignup/NetworkSignup')),
);
const SignupAlpha = withNewLayoutNotLogged(
  withLoadable(() => import('modules/AuthAlpha/containers/Signup/Signup')),
);

const SignupSocialCompletionPage = withNewLayoutNotLogged(
  withLoadable(() => import('modules/AuthAlpha/pages/SignupSocialCompletion')),
);

const PlaybookPage = withNewLayout(
  withLoadable(() => import('modules/LearningAlpha/containers/PlaybookPage')),
);

const LenderCriteriaPage = withNewLayout(
  withLoadable(() => import('modules/LenderAlpha/containers/CriteriaPage')),
);

const LenderCriteriaCreatePage = withNewLayout(
  withLoadable(() => import('modules/LenderAlpha/containers/CriteriaCreatePage')),
);

const LenderCriteriaEditPage = withNewLayout(
  withLoadable(() => import('modules/LenderAlpha/containers/CriteriaEditPage')),
);

const PlaybookContentPage = withNewLayout(
  withLoadable(() => import('modules/LearningAlpha/containers/PlaybookPage')),
);

const LoadableSettingsModule = withLoadable(() => import('../Settings'));
const LoadableUserReportModule = withLoadable(() => import('../UserReport'));

const CreditProfile = withAuth(
  withBusinessLayout(withLoadable(() => import('modules/Business/containers/CreditProfile'))),
);

const CreditProfileOld = withAuth(
  withBusinessLayout(withLoadable(() => import('RemoteBusiness/CreditPage'))),
);

const ComparePlans = withAuth(
  withLoadable(() => import('modules/Business/containers/ComparePlans')),
);

const SubscriptionABPage = withAuth(
  withLoadable(() => import('modules/Business/containers/SubscriptionABPage')),
);

const CreditReview = withAuth(
  withLoadable(() => import('modules/Business/containers/CreditReview')),
);

const CreditReviewAccountant = withAuth(
  withLoadable(() => import('modules/Business/containers/CreditReview/CreditReviewAccountant')),
);

const CreditReviewAccountantSuccess = withAuth(
  withLoadable(() =>
    import('modules/Business/containers/CreditReview/CreditReviewAccountantSuccess'),
  ),
);

const FundingPage = withAuth(
  withBusinessLayout(withLoadable(() => import('modules/Business/containers/FundingPage'))),
);

const CreditReviewSuccess = withAuth(
  withLoadable(() => import('modules/Business/containers/CreditReview/CreditReviewSuccess')),
);

const Director = withAuth(withLoadable(() => import('modules/Business/containers/Director')));
const CheckoutPage = withAuth(withLoadable(() => import('modules/Business/containers/Checkout')));
const DirectorsError = withAuth(
  withLoadable(() => import('modules/Business/containers/DirectorsError')),
);

const DirectorsSuccess = withAuth(
  withLoadable(() => import('modules/Business/containers/DirectorsSuccess')),
);

const PaymentSuccess = withAuth(
  withLoadable(() => import('modules/Business/containers/PaymentSuccess')),
);

const Applications = withAuth(
  withBusinessLayout(withLoadable(() => import('modules/Business/containers/Applications'))),
);

const TodayPage = withAuth(
  withBusinessLayout(withLoadable(() => import('modules/Business/containers/Today'))),
);

const FundingSearchMatchesPageWithBusinessLayout = withAuth(
  withBusinessLayout(
    withLoadable(() =>
      import('modules/FundingSearch/containers/FundingSearchMatchesPage/FundingSearchMatchesPage'),
    ),
    { hideSideMenu: true, injectOrigin: true },
  ),
);
const FundingSearchProfilePageWithBusinessLayout = withAuth(
  withBusinessLayout(
    withLoadable(() =>
      import('modules/Business/containers/FundingSearch/FundingSearchProfilePage'),
    ),
  ),
);

const FundingSearchTimelinePageWithBusinessLayout = withAuth(
  withBusinessLayout(
    withLoadable(() =>
      import(
        'modules/FundingSearch/containers/FundingSearchTimelinePage/FundingSearchTimelinePage'
      ),
    ),
    { hideSideMenu: true, injectOrigin: true },
  ),
);
const CasePageWithBusinessLayout = withAuth(
  withBusinessLayout(
    withLoadable(() => import('modules/Cases/containers/CasePage')),
    {
      hideSideMenu: true,
      injectOrigin: true,
    },
  ),
);

const BusinessReport = withAuth(
  withBusinessLayout(withLoadable(() => import('modules/Business/containers/CapitalReport'))),
);

const BusinessMonitor = withAuth(
  withBusinessLayout(withLoadable(() => import('modules/Business/containers/Monitor'))),
);

export default function Router() {
  const store = useStore();
  useEffect(() => {
    SSEHandler.init(adapterReduxStore(store));
  }, [store]);

  return (
    <Fragment>
      <Switch>
        <Redirect exact from="/" to="/dashboard" />
        <Redirect
          exact
          from="/alpha/signup/:role(business|introducer|lender)?"
          to="/signup/:role(business|introducer|lender)?"
        />
        {!window._env_.production ? (
          <Route exact path="/login" component={LoginPage} />
        ) : (
          <Route exact path="/login" component={LoginRedirect} />
        )}
        {!window._env_.production ? <Route exact path="/callback" component={Callback} /> : null}
        <NotLoggedInRoute exact path="/simulate/type" component={SimulateTypeError} />
        <NotLoggedInRoute exact path="/simulate/chunk" component={SimulateChunkError} />

        <EnhancedRedirect from="/alpha/business/*" to="/business/*" preserveQueryString />
        <LoggedInRoute exact path="/dashboard" component={DashboardPage} />
        <LoggedInRoute exact path="/monitor" component={MonitorWithBaseLayout} />
        <LoggedInRoute exact path="/accountant/review" component={CreditReviewAccountant} />
        <LoggedInRoute exact path="/accountant/success" component={CreditReviewAccountantSuccess} />

        <EnhancedRedirect from="/alpha/business/*" to="/business/*" preserveQueryString />
        <LoggedInRoute exact path="/business/review/success" component={CreditReviewSuccess} />
        <LoggedInRoute exact path="/business/upgrade" component={ComparePlans} />
        <LoggedInRoute exact path="/business/director" component={Director} />
        <LoggedInRoute exact path="/business/checkout" component={CheckoutPage} />
        <LoggedInRoute exact path="/business/billing" component={Billing} />
        <LoggedInRoute exact path="/business/change-plan" component={ComparePlans} />
        <LoggedInRoute exact path="/business/congrats" component={CongratsPage} />
        <LoggedInRoute exact path="/business/review-plan" component={ReviewPage} />
        <LoggedInRoute exact path="/business/discount" component={SubscriptionABPage} />
        <LoggedInRoute exact path="/business/success" component={SuccessPage} />
        <LoggedInRoute exact path="/business/fail" component={FailPage} />
        <LoggedInRoute exact path="/business/upgrade-failed" component={DirectorsError} />
        <LoggedInRoute exact path="/business/upgrade-completed" component={DirectorsSuccess} />
        <LoggedInRoute path="/business/payment-success" component={PaymentSuccess} />
        <LoggedInRoute path="/business/billing-details" component={UserPortal} />
        <LoggedInRoute path="/business/payment-failed" component={FailedPayment} />
        <LoggedInRoute path="/business/payment-succeed" component={SuccessPayment} />
        <LoggedBusinessRoute exact path="/business/monitor" component={BusinessMonitor} />
        <LoggedBusinessRoute exact path="/business/review" component={CreditReview} />
        <LoggedBusinessRoute exact path="/business/funding" component={FundingPage} />
        <LoggedBusinessRoute exact path="/business/credit-profile-old" component={CreditProfileOld} />
        <LoggedBusinessRoute exact path="/business/credit-profile" component={CreditProfile} />
        <LoggedBusinessRoute exact path="/business/applications" component={Applications} />
        <LoggedBusinessRoute exact path="/business/capital-report/:id" component={BusinessReport} />
        <LoggedBusinessRoute
          exact
          path="/business/cases/:id"
          component={CasePageWithBusinessLayout}
        />
        <LoggedBusinessRoute exact path="/business/today" component={TodayPage} />
        <LoggedBusinessRoute exact path="/business/cloud/:step?" component={CloudConnectionPage} />

        <LoggedBusinessRoute
          exact
          path="/business/funding/search/:fundingSearchId/timeline"
          component={FundingSearchTimelinePageWithBusinessLayout}
        />
        <LoggedBusinessRoute
          exact
          path="/business/funding/search/:fundingSearchId/profile/:tab(responses)?"
          component={FundingSearchProfilePageWithBusinessLayout}
        />
        <LoggedBusinessRoute
          exact
          path="/business/funding/search/:fundingSearchId/responses"
          component={FundingSearchMatchesPageWithBusinessLayout}
        />
        <LoggedInRoute exact path="/funding" component={FundingDashboardPageWithBaseLayout} />
        <LoggedInRoute
          exact
          path="/funding/report/:reportId"
          component={FundingReportPageWithBaseLayout}
        />
        <LoggedInRoute
          exact
          path="/funding/search/:fundingSearchId/timeline"
          component={FundingSearchTimelinePageWithBaseLayout}
        />
        <LoggedInRoute
          exact
          path="/funding/search/:fundingSearchId/profile"
          component={FundingSearchProfilePage}
        />
        <LoggedInRoute
          exact
          path="/funding/search/:fundingSearchId/responses"
          component={FundingSearchMatchesPageWithBaseLayout}
        />

        <LoggedInRoute
          exact
          path="/funding/search/:fundingSearchId/match/:fundingMatchId/timeline"
          component={FundingSearchTimelinePageWithBaseLayout}
        />
        <LoggedInRoute
          exact
          path="/funding/search/:fundingSearchId/match/:fundingMatchId/profile"
          component={FundingSearchProfilePage}
        />
        <LoggedInRoute
          exact
          path="/funding/search/:fundingSearchId/match/:fundingMatchId/offers"
          component={FundingSearchOffersPageWithBaseLayout}
        />
        <LoggedInRoute path="/cases/:id" component={CasePageWithBaseLayout} />
        <LoggedInRoute path="/cases" component={CasesPageWithBaseLayout} />
        <LoggedInRoute exact path="/learning" component={LearningPage} />

        <LoggedInRoute exact path="/learning/playbook" component={PlaybookPage} />

        <LoggedInRoute exact path="/learning/playbook/:id/:slug" component={PlaybookContentPage} />

        <LoggedInRoute exact path="/learning/:type" component={CoursePage} />

        <LoggedInRoute exact path="/learning/:type/lesson/:id" component={LessonPage} />
        <LoggedInRoute
          exact
          path="/capital-report/:entityId"
          component={CapitalReportWithBaseLayout}
        />

        <LoggedInRoute exact path="/lender/criteria" component={LenderCriteriaPage} />

        <LoggedInRoute exact path="/lender/criteria/:id" component={LenderCriteriaEditPage} />

        <LoggedInRoute
          exact
          path="/lender/criteria/:type/:product/create"
          component={LenderCriteriaCreatePage}
        />

        <LoggedInRoute path="/settings" component={LoadableSettingsModule} />
        <LoggedInRoute path="/user-report" component={LoadableUserReportModule} />
        <LoggedInRoute
          exact
          path="/questionnaires/:id"
          component={QuestionnairePageWithBaseLayout}
        />
        <NotLoggedInRoute
          exact
          path="/questionnaire/anonymous/:countryCode(GB|ZA)?"
          component={QuestionnairePageNotLogged}
        />
        <NotLoggedInRoute exact path="/teams/invite" component={InvitePage} />
        <NotLoggedInRoute exact path="/alpha/signin" component={Login} />
        <NotLoggedInRoute
          exact
          path="/signup/questionnaire"
          component={SignupQuestionnaireGuardian}
        />
        <NotLoggedInRoute
          exact
          path="/signup/authorize/:token/type/XERO_MARKETPLACE"
          component={InviteXeroPage}
        />
        <NotLoggedInRoute
          exact
          path="/signup/authorize/:token/type/:type"
          component={InviteCompanyPageAlpha}
        />
        <NotLoggedInRoute exact path="/alpha/network/invite" component={InviteNetworkPageAlpha} />
        <NotLoggedInRoute
          exact
          path="/signup/:role(business|introducer|lender)?"
          component={SignupAlpha}
        />
        <NotLoggedInRoute exact path="/teams/invite/reject" component={RejectPage} />
        <NotLoggedInRoute exact path="/mfa-warning" component={MFAWarningPage} />
        <NotLoggedInRoute
          exact
          path="/signup/social-signup"
          component={SignupSocialCompletionPage}
        />
        <NotLoggedInRoute exact path="/signup/:network" component={NetworkSignupPage} />

        <Route exact path="/signup/authorize/:token" component={OpenIdSignupPage} />

        <Route component={NotFound} />
      </Switch>

      <Onboarding />

      <OfferModal />

      <PreRequestModal />

      <NotifyContainer />

      <RequestHelpModal />

      <UpdateEnquiryStatusModal />
    </Fragment>
  );
}
